import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { DesktopDatePicker, DesktopDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField } from '@mui/material';
import frLocale from 'date-fns/locale/fr';
import { translate } from 'utils';
import { isValid } from 'date-fns';

export const Datepicker = ({
                             dataCy, disabled, fullWidth, label, value, onChange, onError, ...config
                           }) => {
  const handleChange = useCallback(date => onChange(date), [onChange]);
  const handleError = useCallback(error => onError(error), [onError]);

  return (
    <LocalizationProvider adapterLocale={frLocale} dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
            cancelText={translate('button.cancel')}
            clearable
            clearText={translate('button.delete')}
            disableFuture={config.disableFuture}
            disableMaskedInput
            disablePast={config.disablePast}
            inputFormat="dd/MM/yyyy"
            label={translate(label)}
            maxDate={config.maxDate || new Date('2100-01-01')}
            maxDateMessage={translate('warnings.date.maxDate')}
            minDate={config.minDate || new Date('1900-01-01')}
            minDateMessage={translate('warnings.date.minDate')}
            renderInput={(params) => (
              <TextField
                    {...params}
                    data-cy={dataCy}
                    disabled={disabled}
                    error={value && !isValid(value)}
                    fullWidth={fullWidth}
                    helperText={value && !isValid(value) && translate('errors.invalidDate')}
                    InputProps={{
                      ...params.InputProps,
                      'aria-label': translate(label)
                    }}
                />
            )}
            showTodayButton
            value={value}
            onChange={handleChange}
            onError={handleError}
            {...config}
        />
    </LocalizationProvider>
  );
};

Datepicker.propTypes = {
  dataCy: PropTypes.string,
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  error: PropTypes.bool,
  fullWidth: PropTypes.bool,
  label: PropTypes.string,
  maxDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  minDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  name: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ])
};

Datepicker.defaultProps = {
  dataCy: '',
  disableFuture: false,
  disablePast: false,
  error: false,
  fullWidth: false,
  label: 'common.date',
  maxDate: new Date('2100-01-01'),
  minDate: new Date('1900-01-01'),
  name: '',
  onChange: () => {},
  onError: () => {},
  required: false,
  value: null
};

export const DateTimepicker = ({
                                 dataCy, label, onChange, value, disabled, fullWidth, ...config
                               }) => {
  const handleChange = useCallback(date => onChange(date), [onChange]);

  return (
    <LocalizationProvider adapterLocale={frLocale} dateAdapter={AdapterDateFns}>
      <DesktopDateTimePicker
            ampm={config.ampm || false}
            animateYearScrolling={true}
            cancelText={translate('button.cancel')}
            clearable
            clearText={translate('button.delete')}
            disableFuture={config.disableFuture}
            disableMaskedInput
            disablePast={config.disablePast}
            inputFormat="dd/MM/yyyy HH:mm"
            label={translate(label)}
            maxDate={config.maxDate || new Date('2100-01-01')}
            maxDateMessage={translate('warnings.date.maxDate')}
            minDate={config.minDate || new Date('1900-01-01')}
            minDateMessage={translate('warnings.date.minDate')}
            renderInput={(params) => (
              <TextField
                    {...params}
                    data-cy={dataCy}
                    disabled={disabled}
                    error={value && !isValid(value)}
                    fullWidth={fullWidth}
                    helperText={value && !isValid(value) && translate('errors.invalidDate')}
                    InputProps={{
                      ...params.InputProps,
                      'aria-label': translate(label)
                    }}
                />
            )}
            showTodayButton
            value={value}
            onChange={handleChange}
            {...config}
        />
    </LocalizationProvider>
  );
};

DateTimepicker.propTypes = {
  ampm: PropTypes.bool,
  dataCy: PropTypes.string,
  disabled: PropTypes.bool,
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  error: PropTypes.bool,
  fullWidth: PropTypes.bool,
  label: PropTypes.string,
  maxDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  minDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  name: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.instanceOf(Date)
};

DateTimepicker.defaultProps = {
  ampm: false,
  dataCy: '',
  disabled: false,
  disableFuture: false,
  disablePast: false,
  error: false,
  fullWidth: false,
  label: 'common.date',
  maxDate: new Date('2100-01-01'),
  minDate: new Date('1900-01-01'),
  name: '',
  onChange: () => {},
  required: false,
  value: ''
};
